// Klienti: seznam s hledáním, řazením a štítkem, načítání dalších stránek a cache detailů.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { AiNoteResponse, ClientSummary, ClientTag, CreateClientInput } from '@wac-buddy/shared'
import {
  createClient,
  deleteClient,
  getClient,
  listClients,
  postClientNote,
  updateClient,
  type ClientDetail,
} from '@/api/clients'

export type ListStatus = 'idle' | 'loading' | 'ready' | 'error'
export type ClientSort = 'activity' | 'name'

const PAGE_SIZE = 50

function toSummary(c: ClientDetail): ClientSummary {
  return {
    id: c.id,
    type: c.type,
    name: c.name,
    phoneE164: c.phoneE164,
    email: c.email,
    city: c.city,
    lastActivityAt: c.lastActivityAt,
    openJobs: c.openJobs,
    tags: c.tags,
  }
}

export const useClientsStore = defineStore('clients', () => {
  const items = ref<ClientSummary[]>([])
  const nextCursor = ref<string | null>(null)
  const status = ref<ListStatus>('idle')
  const loadingMore = ref(false)
  const q = ref('')
  const sort = ref<ClientSort>('activity')
  const tag = ref<ClientTag | undefined>(undefined)
  const details = ref<Record<string, ClientDetail>>({})

  // Odpovědi, které přijdou po novějším dotazu, se zahodí (rychlé psaní v hledání).
  let requestSeq = 0

  /** Načte první stránku podle aktuálního q, sort a tag. */
  async function load(): Promise<void> {
    const seq = ++requestSeq
    status.value = 'loading'
    try {
      const res = await listClients({
        q: q.value,
        sort: sort.value,
        tag: tag.value,
        limit: PAGE_SIZE,
      })
      if (seq !== requestSeq) return
      items.value = res.data
      nextCursor.value = res.nextCursor
      status.value = 'ready'
    } catch {
      if (seq !== requestSeq) return
      status.value = 'error'
    }
  }

  async function loadMore(): Promise<void> {
    if (!nextCursor.value || loadingMore.value || status.value !== 'ready') return
    const seq = requestSeq
    loadingMore.value = true
    try {
      const res = await listClients({
        q: q.value,
        sort: sort.value,
        tag: tag.value,
        cursor: nextCursor.value,
        limit: PAGE_SIZE,
      })
      if (seq !== requestSeq) return
      const known = new Set(items.value.map((c) => c.id))
      items.value = [...items.value, ...res.data.filter((c) => !known.has(c.id))]
      nextCursor.value = res.nextCursor
    } catch {
      /* další stránku si uživatel vyžádá znovu */
    } finally {
      loadingMore.value = false
    }
  }

  async function search(next: string): Promise<void> {
    q.value = next
    await load()
  }

  async function setSort(next: ClientSort): Promise<void> {
    sort.value = next
    await load()
  }

  async function setTag(next: ClientTag | undefined): Promise<void> {
    tag.value = next
    await load()
  }

  /** Detail z cache, nebo ze serveru. `force` obejde cache (po úpravě kontaktu apod.). */
  async function fetchDetail(id: string, force = false): Promise<ClientDetail> {
    const cached = details.value[id]
    if (cached && !force) return cached
    const detail = await getClient(id)
    details.value[id] = detail
    return detail
  }

  function applyDetail(detail: ClientDetail): void {
    details.value[detail.id] = detail
    const i = items.value.findIndex((c) => c.id === detail.id)
    if (i >= 0) items.value[i] = toSummary(detail)
  }

  async function create(input: CreateClientInput): Promise<ClientDetail> {
    const detail = await createClient(input)
    applyDetail(detail)
    items.value = [toSummary(detail), ...items.value.filter((c) => c.id !== detail.id)]
    return detail
  }

  async function update(id: string, input: Parameters<typeof updateClient>[1]) {
    const detail = await updateClient(id, input)
    applyDetail(detail)
    return detail
  }

  /**
   * Poznámka ke klientovi (hlídač slibů): uloží se a Buddy v ní hledá sliby a úkoly. Chyba (429 při
   * vyčerpaném limitu) jde dál. Po uložení se detail načte znovu, aby byla poznámka v časové ose;
   * selhání načtení uloženou poznámku nezneplatní.
   */
  async function addNote(id: string, text: string): Promise<AiNoteResponse> {
    const res = await postClientNote(id, { text })
    try {
      await fetchDetail(id, true)
    } catch {
      /* poznámka je uložená, časová osa se obnoví při dalším načtení detailu */
    }
    return res
  }

  async function remove(id: string): Promise<void> {
    await deleteClient(id)
    delete details.value[id]
    items.value = items.value.filter((c) => c.id !== id)
  }

  /** Po importu a podobných hromadných změnách: seznam se načte znovu a cache detailů se zahodí. */
  function invalidate(): void {
    details.value = {}
    items.value = []
    nextCursor.value = null
    status.value = 'idle'
  }

  return {
    items,
    nextCursor,
    status,
    loadingMore,
    q,
    sort,
    tag,
    details,
    load,
    loadMore,
    search,
    setSort,
    setTag,
    fetchDetail,
    applyDetail,
    create,
    update,
    addNote,
    remove,
    invalidate,
  }
})
